/**
 * WHAT THE REPAIR ORDER SAID THAT OPTIBAY DID *NOT* TURN INTO WORK.
 *
 * A ShopMonkey RO routinely carries the customer's complaint and several priced
 * repair recommendations alongside the one operation that was actually sold.
 * OptiBay deliberately creates no jobs from either — but silently discarding
 * them would be its own failure: the advisor would have no idea the document
 * mentioned a torn axle boot.
 *
 * So they are shown here, plainly labelled as NOT added, next to the jobs that
 * were. Read-only on purpose: this panel never writes to the form, and nothing
 * in it can become a ticket job by being displayed.
 */
import { AlertCircle, MessageSquare, Lightbulb, Wrench } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export interface RoRecommendation {
  description?: string;
  price?: number;
}

export interface RoUnmatchedJob {
  jobName?: string;
  hours?: number;
  notes?: string;
}

export interface RoReviewContextProps {
  sourceFormat?: string;
  customerComments?: string[];
  recommendations?: RoRecommendation[];
  unmatchedJobs?: RoUnmatchedJob[];
  serviceWriter?: string;
  createdDate?: string;
  ticketNumber?: string;
}

const money = (n?: number) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : null;

export const RoReviewContext = ({
  sourceFormat,
  customerComments = [],
  recommendations = [],
  unmatchedJobs = [],
  serviceWriter,
  createdDate,
  ticketNumber,
}: RoReviewContextProps) => {
  const hasComments = customerComments.length > 0;
  const hasRecommendations = recommendations.length > 0;
  const hasUnmatched = unmatchedJobs.length > 0;
  const hasMeta = Boolean(serviceWriter || createdDate || ticketNumber);

  // Nothing to review → render nothing at all, rather than an empty shell that
  // implies the document said nothing.
  if (!hasComments && !hasRecommendations && !hasUnmatched && !hasMeta) return null;

  return (
    <Card className="mb-6 border-dashed" data-testid="ro-review-context">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <AlertCircle className="h-4 w-4 text-muted-foreground" />
          From the repair order — not added as work
          {sourceFormat && (
            <Badge variant="outline" data-testid="ro-review-source-format">
              {sourceFormat}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {hasMeta && (
          <div
            className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
            data-testid="ro-review-meta"
          >
            {ticketNumber && <span>RO #{ticketNumber}</span>}
            {createdDate && <span>Created {createdDate}</span>}
            {serviceWriter && <span>Service writer: {serviceWriter}</span>}
          </div>
        )}

        {hasComments && (
          <div data-testid="ro-review-comments">
            <p className="mb-1 flex items-center gap-2 font-medium">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              Customer comments
              <span className="text-xs font-normal text-muted-foreground">
                (concerns, not jobs)
              </span>
            </p>
            <ul className="list-disc space-y-0.5 pl-6 text-muted-foreground">
              {customerComments.map((c, i) => (
                <li key={`${i}-${c}`}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {hasRecommendations && (
          <div data-testid="ro-review-recommendations">
            <p className="mb-1 flex items-center gap-2 font-medium">
              <Lightbulb className="h-4 w-4 text-muted-foreground" />
              Recommendations
              <span className="text-xs font-normal text-muted-foreground">
                (not authorized — add manually if sold)
              </span>
            </p>
            <ul className="list-disc space-y-0.5 pl-6 text-muted-foreground">
              {recommendations.map((r, i) => {
                const price = money(r.price);
                return (
                  <li key={`${i}-${r.description ?? ''}`}>
                    {r.description}
                    {price && <span className="ml-1 font-medium">{price}</span>}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {hasUnmatched && (
          <div data-testid="ro-review-unmatched">
            <p className="mb-1 flex items-center gap-2 font-medium">
              <Wrench className="h-4 w-4 text-muted-foreground" />
              Sold labor with no catalog match
              <span className="text-xs font-normal text-muted-foreground">
                (read from the RO — add manually)
              </span>
            </p>
            <ul className="list-disc space-y-0.5 pl-6 text-muted-foreground">
              {unmatchedJobs.map((j, i) => (
                <li key={`${i}-${j.jobName ?? ''}`}>
                  {j.jobName}
                  {typeof j.hours === 'number' && (
                    <span className="ml-1 font-medium">{j.hours} hr</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
